import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PaymentsIcon from '@mui/icons-material/Payments';
import api from '../api/client';
import StatCard from '../components/StatCard';
import { formatMoney } from '../components/Money';

const dateLabel = (iso, lang) =>
  new Date(iso).toLocaleDateString(lang === 'en' ? 'en-IN' : lang + '-IN', {
    day: 'numeric',
    month: 'short',
  });

/** One worker's full wage ledger: totals + attendance + payment history */
const WorkerLedger = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/api/workers/${id}/ledger`).then((r) => setData(r.data)).catch(() => {});
  }, [id]);

  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const { worker, attendance, payments, earned, paid, balance } = data;
  const daysWorked = attendance.filter((a) => a.status !== 'absent').length;

  /* merge attendance + payments into one history feed, newest first */
  const history = [
    ...attendance.map((a) => ({ kind: 'attendance', date: a.date, row: a })),
    ...payments.map((p) => ({ kind: 'payment', date: p.date, row: p })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} edge="start">
          <ArrowBackIcon />
        </IconButton>
        <Avatar sx={{ bgcolor: 'secondary.main', color: '#fff' }}>{worker.name[0]?.toUpperCase()}</Avatar>
        <Box>
          <Typography variant="h6">{worker.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {worker.skill ? `${worker.skill} · ` : ''}₹{Number(worker.daily_rate)}/day · {daysWorked}{' '}
            {t('worker.days')}
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <StatCard label={t('worker.earned')} value={formatMoney(earned)} color="success.main" />
        <StatCard label={t('worker.paid')} value={formatMoney(paid)} />
        <StatCard
          label={balance >= 0 ? t('worker.balance') : t('worker.advanceGiven')}
          value={formatMoney(Math.abs(balance))}
          color={balance > 0 ? 'error.main' : 'success.main'}
        />
      </Stack>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {t('worker.history')}
      </Typography>
      <Card>
        {history.length === 0 && (
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">—</Typography>
          </CardContent>
        )}
        {history.map((item, idx) => (
          <Stack
            key={`${item.kind}-${item.row.id}`}
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={(theme) => ({
              px: 2,
              py: 1.25,
              borderTop: idx > 0 ? `1px solid ${theme.palette.divider}` : 'none',
            })}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'action.hover' }}>
              {item.kind === 'attendance' ? (
                <EventAvailableIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              ) : (
                <PaymentsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              )}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle2">
                {item.kind === 'attendance'
                  ? item.row.status === 'full'
                    ? t('worker.fullDay')
                    : item.row.status === 'half'
                      ? t('worker.halfDay')
                      : t('worker.absent')
                  : item.row.type === 'advance'
                    ? t('worker.advance')
                    : t('worker.payment')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dateLabel(item.date, i18n.language)}
                {item.row.note ? ` · ${item.row.note}` : ''}
              </Typography>
            </Box>
            {item.kind === 'attendance' ? (
              Number(item.row.wage_amount) > 0 && (
                <Typography variant="subtitle2" color="success.main">
                  +{formatMoney(item.row.wage_amount)}
                </Typography>
              )
            ) : (
              <Stack alignItems="flex-end" spacing={0.25}>
                <Typography variant="subtitle2">−{formatMoney(item.row.amount)}</Typography>
                {item.row.type === 'advance' && (
                  <Chip size="small" variant="outlined" color="warning" label={t('worker.advance')} sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }} />
                )}
              </Stack>
            )}
          </Stack>
        ))}
      </Card>
    </Box>
  );
};

export default WorkerLedger;
